import type { VendorRow } from "@checkone/contract/client";
import { useRef } from "react";

/**
 * How often a page with a vendor showing "Checking…" asks again, and for how long it keeps asking (the
 * worker's check lands about 15 s after the last edit). Mutable only so tests can poll faster.
 */
export const CHECKING_POLL = { everyMs: 3_000, forMs: 3 * 60_000 };

type Row = Pick<VendorRow, "id" | "updated_at" | "lists">;

/**
 * A vendor whose NTN or CNIC as they are now has not been checked yet: just added, edited or given an
 * identifier from a name suggestion. The API has queued its check (worker `vendor_check`), which lands a
 * few seconds after the last edit.
 */
export function isChecking(v: Pick<VendorRow, "lists">): boolean {
  return v.lists.ATL_IT.reason === "pending_first_check" || v.lists.ATL_ST.reason === "pending_first_check";
}

/**
 * The `refetchInterval` decision, pure: poll while any vendor is checking, for at most
 * CHECKING_POLL.forMs from when that set of vendors (or their last edit) first appeared, so a check that
 * never lands (the Monday run catches it) does not poll for ever. A new edit restarts the window.
 */
export function checkingInterval(
  rows: readonly Row[],
  state: { key: string; since: number },
  now: number,
): number | false {
  const key = rows
    .filter(isChecking)
    .map((r) => `${r.id}@${r.updated_at}`)
    .join(",");
  if (!key) {
    state.key = "";
    return false;
  }
  if (key !== state.key) {
    state.key = key;
    state.since = now;
  }
  return now - state.since < CHECKING_POLL.forMs ? CHECKING_POLL.everyMs : false;
}

/**
 * `refetchInterval` for a query whose data holds vendor rows: the vendor shows "Checking…" and then its
 * status, without a reload. TanStack Query pauses it while the tab is hidden.
 */
export function useCheckingPoll<T>(rowsOf: (data: T) => readonly Row[]) {
  const state = useRef({ key: "", since: 0 });
  return (q: { state: { data?: T | undefined } }): number | false =>
    q.state.data === undefined ? false : checkingInterval(rowsOf(q.state.data), state.current, Date.now());
}
